import type { ComponentProps, ReactNode } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import {
  default as Avatar,
  Image as AvatarImage,
  type AvatarSizeToken,
} from '@/components/Custom/Avatar';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import Pressable from '@/components/Unistyles/Pressable';
import Text from '@/components/Unistyles/Text';
import getOffsetColor from '@/utils/getOffsetColor';
import { selectionAsync } from '@/utils/Haptics';
import palette from '@/utils/palette';
import pickAvatar from '@/utils/pickAvatar';

type AvatarColorPickerProps = {
  avatarId?: string | null;
  avatarSize?: AvatarSizeToken;
  children: ReactNode;
  color?: string | null;
  localAvatarUrl?: string | null;
  name: string;
  nameSize?: ComponentProps<typeof Text>['size'];
  nameNumberOfLines?: number;
  onChangeColor(color: string): void;
  onChangeLocalAvatarUrl(localAvatarUrl: string): void;
};

export default function AvatarColorPicker({
  onChangeColor,
  ...props
}: AvatarColorPickerProps) {
  return (
    <Box center space="md" style={styles.container}>
      <AvatarPicker {...props} />
      <AvatarColorPalette color={props.color} onChangeColor={onChangeColor} />
    </Box>
  );
}

export function AvatarPicker({
  avatarId,
  avatarSize = '2xl',
  children,
  color,
  localAvatarUrl,
  name,
  nameSize = '3xl',
  nameNumberOfLines = 1,
  onChangeLocalAvatarUrl,
}: Omit<AvatarColorPickerProps, 'onChangeColor'>) {
  const selectedColor = color || palette[0];
  const onPressAvatar = async () => {
    const image = await pickAvatar();
    if (image?.uri) onChangeLocalAvatarUrl(image.uri);
  };
  return (
    <Box center space="md" style={styles.picker}>
      <Pressable
        accessibilityLabel="Upload avatar"
        accessibilityRole="button"
        onPress={onPressAvatar}
        styleOverride={({ pressed }) => styles.avatarPressable(pressed)}
      >
        <Avatar color={selectedColor} size={avatarSize}>
          {children}
          {localAvatarUrl || avatarId ? (
            <AvatarImage
              id={avatarId || undefined}
              localUrl={localAvatarUrl}
              size={avatarSize}
            />
          ) : null}
        </Avatar>
      </Pressable>

      <Text size={nameSize} bold center numberOfLines={nameNumberOfLines}>
        {name}
      </Text>
    </Box>
  );
}

export function AvatarColorPalette({
  color,
  onChangeColor,
}: Pick<AvatarColorPickerProps, 'color' | 'onChangeColor'>) {
  const selectedColor = color || palette[0];
  return (
    <Box
      direction="horizontal"
      flexWrap="wrap"
      center
      style={styles.palette}
      testID="AvatarColorPicker"
    >
      {palette.map((paletteColor) => {
        const selected = paletteColor === selectedColor;

        return (
          <Pressable
            accessibilityLabel={`Choose ${paletteColor}`}
            accessibilityRole="button"
            accessibilityState={{ selected }}
            key={paletteColor}
            onPress={() => {
              selectionAsync();
              onChangeColor(paletteColor);
            }}
            styleOverride={({ pressed }) =>
              styles.swatch(paletteColor, selected, pressed)
            }
            testID="ColorPickerItem"
          >
            {selected ? (
              <Icon
                color={getOffsetColor(paletteColor, true)}
                name="checkmark"
                size="md"
              />
            ) : null}
          </Pressable>
        );
      })}
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  picker: {
    width: '100%',
  },
  avatarPressable: (pressed: boolean) => ({
    borderRadius: theme.borderRadius.full,
    opacity: pressed ? 0.7 : 1,
    _web: {
      _hover: {
        opacity: 0.9,
      },
    },
  }),
  container: {
    paddingVertical: theme.space('lg'),
    width: '100%',
  },
  palette: {
    gap: theme.size(1.5),
    maxWidth: theme.size(76),
  },
  swatch: (color: string, selected: boolean, pressed: boolean) => ({
    alignItems: 'center',
    backgroundColor: color,
    borderColor: selected ? theme.colors.typography : theme.colors.borderMuted,
    borderRadius: theme.borderRadius.full,
    borderWidth: selected ? theme.size(0.75) : theme.size(0.5),
    height: theme.size(7),
    justifyContent: 'center',
    opacity: pressed ? 0.7 : 1,
    width: theme.size(7),
    _web: {
      _hover: {
        opacity: 0.8,
      },
    },
  }),
}));
